import * as E from "fp-ts/Either"
import axios from "axios"
import { BehaviorSubject, Subject } from "rxjs"
import { Ref, ref, watch } from "vue"

import { getService } from "@hoppscotch/common/modules/dioc"
import {
  AuthEvent,
  AuthPlatformDef,
  HoppUser,
} from "@hoppscotch/common/platform/auth"
import { PersistenceService } from "@hoppscotch/common/services/persistence"

import { getAllowedAuthProviders, updateUserDisplayName } from "./api"

export const authEvents$ = new Subject<AuthEvent>()
const currentUser$ = new BehaviorSubject<HoppUser | null>(null)
export const probableUser$ = new BehaviorSubject<HoppUser | null>(null)

const persistenceService = getService(PersistenceService)
let authGeneration = 0
let signingOut = false
let refreshPromise: Promise<boolean> | null = null

async function logout() {
  await axios.get(`${import.meta.env.VITE_BACKEND_API_URL}/auth/logout`, {
    withCredentials: true,
    adapter: "fetch",
    fetchOptions: { cache: "no-store" },
    headers: { "User-Agent": false },
  })
}

async function signInUserWithGithubFB() {
  window.location.href = `${import.meta.env.VITE_BACKEND_API_URL}/auth/github`
}

async function signInUserWithGoogleFB() {
  window.location.href = `${import.meta.env.VITE_BACKEND_API_URL}/auth/google`
}

async function signInUserWithMicrosoftFB() {
  window.location.href = `${
    import.meta.env.VITE_BACKEND_API_URL
  }/auth/microsoft`
}

async function getInitialUserDetails() {
  const res = await axios.post<{
    data?: {
      me?: {
        uid: string
        displayName: string
        email: string
        photoURL: string
        isAdmin: boolean
        createdOn: string
        // emailVerified: boolean
      }
    }
    errors?: Array<{
      message: string
    }>
  }>(
    `${import.meta.env.VITE_BACKEND_GQL_URL}`,
    {
      query: `query Me {
      me {
        uid
        displayName
        email
        photoURL
        isAdmin
        createdOn
      }
    }`,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  )

  return res.data
}

const isGettingInitialUser: Ref<null | boolean> = ref(null)

async function setUser(user: HoppUser | null) {
  const generation = authGeneration
  const previousUser = currentUser$.value ?? probableUser$.value
  if (previousUser && previousUser.uid !== user?.uid) {
    currentUser$.next(null)
    probableUser$.next(null)
    const { clearAuthenticatedUserData } = await import("./session")
    await clearAuthenticatedUserData()
  }
  if (generation !== authGeneration) return
  currentUser$.next(user)
  probableUser$.next(user)

  await persistenceService.setLocalConfig("login_state", JSON.stringify(user))
}

async function setInitialUser(allowRefresh = true) {
  const generation = authGeneration
  isGettingInitialUser.value = true
  try {
    const res = await getInitialUserDetails()
    if (generation !== authGeneration) return
    const error = res.errors?.[0]

    if (error?.message === "Unauthorized" && allowRefresh) {
      const refreshed = await refreshToken()
      if (generation !== authGeneration) return
      if (refreshed) return await setInitialUser(false)
    }

    if (error || !res.data?.me) {
      await setUser(null)
      authEvents$.next({ event: "logout" })
      return
    }

    const backendUser = res.data.me
    const user: HoppUser = {
      uid: backendUser.uid,
      displayName: backendUser.displayName,
      email: backendUser.email,
      photoURL: backendUser.photoURL,
      emailVerified: true,
    }
    await setUser(user)
    if (generation !== authGeneration) return
    authEvents$.next({ event: "login", user })
  } finally {
    isGettingInitialUser.value = false
  }
}

async function refreshToken() {
  if (signingOut) return false
  if (refreshPromise) return refreshPromise
  const generation = authGeneration

  const refresh = async () => {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_API_URL}/auth/refresh`,
        {
          withCredentials: true,
          // Bypass even an existing cache entry: Chrome can otherwise wait
          // 20 seconds for another refresh request's HTTP cache lock.
          adapter: "fetch",
          fetchOptions: { cache: "no-store" },
          // Keep the browser's User-Agent; Axios's override triggers CORS
          // preflights in Firefox/WebKit that our API intentionally rejects.
          headers: { "User-Agent": false },
        }
      )
      if (generation !== authGeneration) return false
      const successful = res.status === 200
      if (successful) authEvents$.next({ event: "token_refresh" })
      return successful
    } catch {
      return false
    }
  }

  const pending = refresh().finally(() => {
    if (refreshPromise === pending) refreshPromise = null
  })
  refreshPromise = pending
  return pending
}

async function sendMagicLink(email: string) {
  const res = await axios.post(
    `${import.meta.env.VITE_BACKEND_API_URL}/auth/signin`,
    {
      email,
    },
    {
      withCredentials: true,
    }
  )

  if (res.data && res.data.deviceIdentifier) {
    await persistenceService.setLocalConfig(
      "deviceIdentifier",
      res.data.deviceIdentifier
    )
  } else {
    throw new Error("test: does not get device identifier")
  }

  return res.data
}

export const def: AuthPlatformDef = {
  getCurrentUserStream: () => currentUser$,
  getAuthEventsStream: () => authEvents$,
  getProbableUserStream: () => probableUser$,

  getCurrentUser: () => currentUser$.value,
  getProbableUser: () => probableUser$.value,

  getBackendHeaders() {
    return {}
  },
  getGQLClientOptions() {
    return {
      fetchOptions: {
        credentials: "include",
      },
    }
  },

  axiosPlatformConfig() {
    return {
      // for including cookies in the request
      withCredentials: true,
    }
  },

  /**
   * it is not possible for us to know if the current cookie is expired because we cannot access http-only cookies from js
   * hence just returning if the currentUser$ has a value associated with it
   */
  willBackendHaveAuthError() {
    return !currentUser$.value
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBackendGQLClientShouldReconnect(func) {
    authEvents$.subscribe((event) => {
      if (
        event.event == "login" ||
        event.event == "logout" ||
        event.event == "token_refresh"
      ) {
        func()
      }
    })
  },

  /**
   * we cannot access our auth cookies from javascript, so leaving this as null
   */
  getDevOptsBackendIDToken() {
    return null
  },
  async performAuthInit() {
    const probableUser = JSON.parse(
      (await persistenceService.getLocalConfig("login_state")) ?? "null"
    )
    probableUser$.next(probableUser)
    await setInitialUser()
  },

  waitProbableLoginToConfirm() {
    if (currentUser$.value) return Promise.resolve()
    if (isGettingInitialUser.value === false || !probableUser$.value) {
      return Promise.reject(new Error("no_probable_user"))
    }

    return new Promise<void>((resolve, reject) => {
      const unwatch = watch(isGettingInitialUser, (pending) => {
        if (pending !== false) return
        unwatch()
        if (currentUser$.value) resolve()
        else reject(new Error("no_probable_user"))
      })
    })
  },

  async signInWithEmail(email: string) {
    await sendMagicLink(email)
  },

  isSignInWithEmailLink(url: string) {
    const urlObject = new URL(url)
    const searchParams = new URLSearchParams(urlObject.search)

    return !!searchParams.get("token")
  },

  async verifyEmailAddress() {
    return
  },
  async signInUserWithGoogle() {
    await signInUserWithGoogleFB()
  },
  async signInUserWithGithub() {
    await signInUserWithGithubFB()
    return undefined
  },
  async signInUserWithMicrosoft() {
    await signInUserWithMicrosoftFB()
  },
  async signInWithEmailLink(email: string, url: string) {
    const urlObject = new URL(url)
    const searchParams = new URLSearchParams(urlObject.search)

    const token = searchParams.get("token")

    const deviceIdentifier =
      await persistenceService.getLocalConfig("deviceIdentifier")

    await axios.post(
      `${import.meta.env.VITE_BACKEND_API_URL}/auth/verify`,
      {
        token: token,
        deviceIdentifier,
      },
      {
        withCredentials: true,
      }
    )
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async setEmailAddress(_email: string) {
    return
  },

  async setDisplayName(name: string) {
    if (!name)
      return E.left({
        type: "gql_error" as const,
        error: "USER_NAME_CANNOT_BE_EMPTY",
      })
    const user = currentUser$.value
    if (!user)
      return E.left({ type: "gql_error" as const, error: "NO_USER_LOGGED_IN" })

    const res = await updateUserDisplayName(name)
    if (currentUser$.value !== user)
      return E.left({ type: "gql_error" as const, error: "NO_USER_LOGGED_IN" })

    if (E.isRight(res)) {
      await setUser({
        ...user,
        displayName: res.right.updateDisplayName.displayName ?? null,
      })

      return E.right(undefined)
    }
    return E.left(res.left)
  },

  async signOutUser() {
    authGeneration++
    signingOut = true
    const clearing = setUser(null)
    authEvents$.next({ event: "logout" })

    let userCacheCleared = false
    try {
      await clearing
      userCacheCleared = true
    } finally {
      // A refresh already in flight can set cookies even if its result is
      // ignored. Let it finish before the server clears/revokes those cookies.
      await refreshPromise
      try {
        await logout()
      } finally {
        // Keep the previous owner marker if a local write failed so the next
        // login retries cleanup rather than treating old credentials as anonymous.
        if (userCacheCleared)
          await persistenceService.removeLocalConfig("login_state")
        signingOut = false
      }
    }
  },

  async refreshAuthToken() {
    return refreshToken()
  },

  async processMagicLink() {
    if (this.isSignInWithEmailLink(window.location.href)) {
      const deviceIdentifier =
        await persistenceService.getLocalConfig("deviceIdentifier")

      if (!deviceIdentifier) {
        throw new Error(
          "Device Identifier not found, you can only signin from the browser you generated the magic link"
        )
      }

      await this.signInWithEmailLink(deviceIdentifier, window.location.href)

      await persistenceService.removeLocalConfig("deviceIdentifier")
      window.location.href = "/"
    }
  },
  getAllowedAuthProviders,

  /**
   * Verifies if the current user's authentication tokens are valid
   * @returns True if tokens are valid, false otherwise
   */
  async verifyAuthTokens() {
    try {
      const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL

      const response = await axios.get(`${BACKEND_API_URL}/auth/verify-token`, {
        headers: {
          "Content-Type": "application/json",
          ...this.getBackendHeaders(),
        },
        withCredentials: true,
      })

      // axios automatically throws on error status codes, so if we reach here, it was successful
      return !!response.data.isValid
    } catch (_error) {
      return false
    }
  },
}
